import { z } from 'zod';
import { countryCodes, isValidCountryCode } from '../countries';
import {
  DEFAULT_AVAILABLE_COUNTRIES_MODE,
  assertValidAvailableCountries,
  normalizeAvailableCountriesFormValue,
} from '../availableCountries';
import {
  MAX_DAPP_NAME_LENGTH,
  MAX_EDITORS_CHOICE_HEADLINE_LENGTH,
  MAX_EDITORS_CHOICE_TAG_LENGTH,
  MAX_SUBTITLE_LENGTH,
} from '../../constants/form';
import { resolveToAndroidLocale } from '../android-languages';
import { NEW_PACKAGE_INPUT_REGEX } from '../../utils/dappRoutes';

const httpsUrlBaseSchema = z
  .string()
  .url('Invalid URL format')
  .refine((url) => url.startsWith('https://'), {
    message: 'URL must use HTTPS protocol',
  });

export const ANDROID_PACKAGE_NAME_REGEX = NEW_PACKAGE_INPUT_REGEX;
export function getPackageNameFormatError(): string {
  return 'Package name must be in reverse domain format (e.g., com.example.app)';
}

export const requiredHttpsUrlSchema = httpsUrlBaseSchema;

const httpsUrlSchema = httpsUrlBaseSchema.optional().or(z.literal(''));

const optionalEmailSchema = z.preprocess((val) => {
  if (typeof val === 'string') {
    const trimmed = val.trim();
    return trimmed === '' ? null : trimmed;
  }
  return val;
}, z.string().email('Invalid email format').nullable().optional());

const requiredEmailSchema = z
  .string()
  .email('Invalid email format')
  .min(1, 'Email is required');

const contactEmailSchema = z.preprocess((val) => {
  if (typeof val === 'string') {
    return val.trim();
  }
  return val;
}, z.string().min(1, 'Contact email is required').email('Invalid email format'));

const tokenDistributionSchema = z.preprocess(
  (val) => val === 'on' || val === 'true' || val === true,
  z.boolean(),
);

const languagesSchema = z
  .array(z.string())
  .min(1, 'At least one language is required')
  .transform((langs) => {
    const resolvedLangs = langs
      .map((lang) => resolveToAndroidLocale(lang))
      .filter((lang): lang is string => lang !== null);
    return resolvedLangs.length > 0 ? resolvedLangs : ['en-US'];
  });
const availableCountriesSchema = z.preprocess(
  normalizeAvailableCountriesFormValue,
  z.array(z.string()).superRefine((codes, ctx) => {
    const invalidCountryCodes = assertValidAvailableCountries(codes);

    for (const code of invalidCountryCodes) {
      if (!isValidCountryCode(code)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Invalid country code: ${code}`,
        });
      }
    }
  }),
);

const baseDappFormSchema = z
  .object({
    dappName: z
      .string()
      .min(1, 'dApp Name is required')
      .max(MAX_DAPP_NAME_LENGTH),
    subtitle: z.string().max(MAX_SUBTITLE_LENGTH).optional(),
    description: z.string().min(1, 'Description is required'),
    languages: languagesSchema,
    availableCountriesMode: z
      .enum(['include', 'exclude'])
      .default(DEFAULT_AVAILABLE_COUNTRIES_MODE),
    availableCountries: availableCountriesSchema.default([]),
    appWebsite: httpsUrlSchema,
    supportEmail: requiredEmailSchema,
    tokenDistribution: tokenDistributionSchema,
    walletAddress: z.string().optional(),
    licenseUrl: httpsUrlSchema,
    privacyPolicyUrl: httpsUrlSchema,
    editorsChoiceHeadline: z
      .string()
      .max(MAX_EDITORS_CHOICE_HEADLINE_LENGTH)
      .optional(),
    editorsChoiceTag: z.string().max(MAX_EDITORS_CHOICE_TAG_LENGTH).optional(),
  })
  .superRefine((data, ctx) => {
    if (
      data.availableCountriesMode === 'exclude' &&
      data.availableCountries.length >= countryCodes.length
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['availableCountries'],
        message: 'At least one country must remain available',
      });
    }
  });

export const newDappFormSchema = baseDappFormSchema.extend({
  packageName: z
    .string()
    .min(1, 'Package name is required')
    .refine((value) => ANDROID_PACKAGE_NAME_REGEX.test(value), {
      error: getPackageNameFormatError,
    }),
  contactEmail: contactEmailSchema,
  connectedWalletAddress: z.string().optional(),
});

export const updateDappFormSchema = baseDappFormSchema.extend({
  packageName: z.string().optional(),
  contactEmail: optionalEmailSchema,
});

export type NewDappFormData = z.infer<typeof newDappFormSchema>;
export type UpdateDappFormData = z.infer<typeof updateDappFormSchema>;
